import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Search, X } from 'lucide-react'
import { matcher, type BusinessChoice } from '@/lib/discovery'
import type { DiscoveryCatalog } from '@/types'

// ---------------------------------------------------------------------------
// "What's your business?" — the one optional fact that tunes the Builder Hub:
// which ideas are popular, which are "made for" the user, and the guidance the
// questions get. Every industry in the catalogue, grouped by sector, with
// keyword search ("garage", "shaadi", "clinic"); a business that is not listed
// can be used as typed (id null — it reaches the brief as the user's own
// words, never as a catalogue id); Skip clears it.
// ---------------------------------------------------------------------------

interface Props {
  open: boolean
  catalog: DiscoveryCatalog
  currentId: string | null
  /** Heading override, e.g. when asked as the first step of the questions. */
  title?: string
  onPick: (choice: BusinessChoice | null) => void
  onClose: () => void
}

export default function IndustryPicker({ open, catalog, currentId, title, onPick, onClose }: Props) {
  const [q, setQ] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  // Read through a ref so a parent's inline onClose doesn't re-run the
  // open effect (which would clear the search on every parent render).
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!open) return
    setQ('')
    const t = setTimeout(() => searchRef.current?.focus({ preventScroll: true }), 0)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey) }
  }, [open])

  if (!open) return null

  const hit = matcher(q)
  const groups = catalog.sectors
    .map((s) => ({ name: s.name, inds: s.industries.filter((i) => hit(`${i.name} ${i.keywords} ${s.name}`)) }))
    .filter((g) => g.inds.length)
  const total = catalog.sectors.reduce((n, s) => n + s.industries.length, 0)
  const typed = q.trim()

  return createPortal(
    <div style={backdrop} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="biz-title" style={dialog} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '20px 20px 14px', display: 'flex', flexDirection: 'column', gap: 14, borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <h2 id="biz-title" style={{ margin: 0, fontSize: '1.3125rem', fontWeight: 600 }}>{title || "What's your business?"}</h2>
              <p style={{ margin: '4px 0 0', fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
                We'll suggest ideas that fit and use it when building. You can change it any time.
              </p>
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <div style={searchBox}>
            <Search size={17} style={{ color: 'var(--color-text-faint)', flex: 'none' }} />
            <input
              ref={searchRef}
              type="text"
              autoComplete="off"
              aria-label="Search businesses"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && typed && !groups.length) onPick({ id: null, name: typed })
              }}
              placeholder="Search — try clinic, garage, school, distributor, wedding"
              style={searchInput}
            />
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 20px 18px' }}>
          {groups.map((g) => (
            <div key={g.name} style={{ paddingTop: 14 }}>
              <h3 style={sectorTitle}>{g.name}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {g.inds.map((i) => (
                  <button key={i.id} type="button" aria-pressed={currentId === i.id}
                    onClick={() => onPick({ id: i.id, name: i.name })} style={chip(currentId === i.id)}>
                    {i.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!groups.length && (
            <div style={{ padding: '28px 0', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>Nothing listed matches “{typed}” — that's fine.</span>
              <button type="button" className="btn btn-primary" onClick={() => onPick({ id: null, name: typed })}>
                Use “{typed}” as my business
              </button>
            </div>
          )}
        </div>

        <div style={footer}>
          <span style={{ fontSize: '0.8625rem', color: 'var(--color-text-faint)' }}>
            {total} businesses in {catalog.sectors.length} sectors
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onPick(null)}>
            Skip — show ideas for every business
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ── styles ────────────────────────────────────────────────────────────
const backdrop: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(14,17,23,.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
}
const dialog: CSSProperties = {
  width: '100%', maxWidth: 760, maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column',
  background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)',
  borderRadius: 14, boxShadow: '0 24px 64px rgba(0,0,0,.28)', overflow: 'hidden',
}
const searchBox: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, height: 44, padding: '0 12px',
  border: '1px solid var(--color-border-strong)', borderRadius: 10, background: 'var(--color-input-bg, var(--color-bg))',
}
// Drop the global field chrome (border, focus ring, padding) inside the box.
const searchInput: CSSProperties = {
  flex: 1, minWidth: 0, height: '100%', border: 0, borderRadius: 0, padding: 0, boxShadow: 'none',
  outline: 'none', background: 'transparent', font: 'inherit',
  fontSize: '1.0125rem', color: 'var(--color-text)',
}
const sectorTitle: CSSProperties = {
  margin: '0 0 8px', fontSize: '0.8425rem', fontWeight: 600, color: 'var(--color-text-secondary)',
}
const footer: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
  padding: '12px 20px', borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
}
const chip = (on: boolean): CSSProperties => ({
  font: 'inherit', fontSize: '0.9225rem', borderRadius: 999, padding: '7px 13px', cursor: 'pointer',
  textAlign: 'left', minHeight: 36,
  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
  background: on ? 'var(--color-accent)' : 'var(--color-bg)',
  color: on ? '#fff' : 'var(--color-text)',
})
